// Pure snapshot builder for ExportInput — reads the live session/mixer/video
// stores at call time and assembles the data needed by audioExport /
// videoExport pipelines AND by the upload pipeline. Extracted from
// useExport.snapshotExportInput so TTSView/STSView can build the same
// ExportInput for upload without going through the export hook.
//
// Pipelines are long-running — the snapshot must be taken at the moment the
// run starts so concurrent user edits (block text, mixer levels, navigation)
// can't taint the output.

import type { ExportInput, VoiceClipInput } from '@/lib/audioExport';
import { resolveBedPlacement, type BedPlacement } from './bedPlacement';
import { ensureMediaSession, streamVideoUrl } from '@/lib/api';
import {
  effectiveSfxVolume,
  effectiveVideoVolume,
  effectiveVoiceVolume,
  useMixerStore,
} from '@/stores/mixer';
import { isStsMode } from '@/lib/assetMode';
import { isFlexAsset } from '@/lib/assetUi';
import { selectAllAssets, useSessionStore } from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import { lastFavClipEnd, resolveTimelineLength } from '@/lib/timelineLength';

/** Build voice clips from a TTS asset's block list — one clip per block that
 *  has a favorite take with a decodable buffer. */
function collectVoiceClips(
  blocks: ReturnType<typeof useSessionStore.getState>['assetBlocks'][number],
): VoiceClipInput[] {
  const clips: VoiceClipInput[] = [];
  if (!blocks) return clips;
  for (const b of blocks) {
    if (b.favIdx < 0) continue;
    const take = b.takes[b.favIdx];
    if (!take?.buffer) continue;
    clips.push({ audio: take.buffer, offset: b.offset ?? 0 });
  }
  return clips;
}

/** Build voice clips from an STS asset's master timeline. Each MasterClip
 *  carries its own in/out/offset against the source take. */
function collectSTSVoiceClips(
  state:
    | ReturnType<typeof useSessionStore.getState>['stsAssets'][number]
    | undefined,
): VoiceClipInput[] {
  const clips: VoiceClipInput[] = [];
  if (!state) return clips;
  const byId = new Map(state.takes.map((t) => [t.id, t]));
  for (const clip of state.master) {
    const take = byId.get(clip.takeId);
    if (!take?.audioBuffer) continue;
    clips.push({
      audio: take.audioBuffer,
      offset: clip.offset,
      inPoint: clip.inPoint,
      outPoint: clip.outPoint,
    });
  }
  return clips;
}

/** End-of-last-voice on the timeline in seconds. Voice can be placed past the
 *  asset/video duration; the display timeline still honors the asset length,
 *  but the export must include the full voice tail or the rendered WAV cuts
 *  off mid-word (renderers slice on `Math.min(sliceLen, totalDuration -
 *  startAt)`). For STS the take slice is `outPoint - inPoint`; for TTS we use
 *  the take's stored `duration` (the decoded buffer isn't available here
 *  synchronously). */
function lastVoiceClipEnd(
  state: ReturnType<typeof useSessionStore.getState>,
  assetIdx: number,
  isStsActive: boolean,
): number {
  let lastEnd = 0;
  if (isStsActive) {
    const sts = state.stsAssets[assetIdx];
    if (!sts) return 0;
    for (const clip of sts.master) {
      const sliceLen = Math.max(0, clip.outPoint - clip.inPoint);
      const end = clip.offset + sliceLen;
      if (end > lastEnd) lastEnd = end;
    }
  } else {
    lastEnd = lastFavClipEnd(state.assetBlocks[assetIdx] ?? []);
  }
  return lastEnd;
}

export interface ExportSnapshot {
  exportInput: ExportInput;
  assetTitle: string | undefined;
  assetType: 'tts' | 'sts' | 'free' | undefined;
  /** Resolved active mode for the asset at snapshot time. For 'tts' and
   *  'sts' assets this matches `assetType`; for 'free' assets it follows
   *  `freeMode[idx]` (the user's TTS/STS pill choice). Use this for any
   *  TTS-vs-STS dispatch downstream (e.g. error-message wording). */
  effectiveType: 'tts' | 'sts' | undefined;
  videoSrc: string | null;
  videoBlob: Blob | null;
  videoPath: string | null | undefined;
  toolId: string | null;
}

/** Snapshot every store-derived field needed for an export/upload run. The
 *  pipelines that consume this don't touch the stores directly — they only
 *  read this object, so concurrent edits during a long render won't taint
 *  the result. */
export function snapshotExportInput(assetIdx: number): ExportSnapshot {
  const session = useSessionStore.getState();
  const mixer = useMixerStore.getState();
  const video = useVideoStore.getState();
  const assets = selectAllAssets(session);
  const asset = assets[assetIdx];

  // STS dispatch: explicit STS asset, OR a free / custom asset whose user-
  // chosen mode is STS (FreeChoiceView routes those into <STSView>, where
  // the timeline + ExportBar live, but `assetType` stays 'free'). Without
  // the freeMode check the export pipeline would fall through to the TTS
  // branch and read whatever stale blocks happen to sit in
  // `assetBlocks[idx]` from a prior TTS-mode generation — yielding the
  // wrong audio for the active STS take pool.
  const isStsActive = isStsMode(
    asset?.assetType,
    session.freeMode[assetIdx] as 'tts' | 'sts' | undefined,
  );

  const voiceClips = isStsActive
    ? collectSTSVoiceClips(session.stsAssets[assetIdx])
    : collectVoiceClips(session.assetBlocks[assetIdx] ?? []);

  // Longest of all loaded beds (max 2) — same fallback semantics as the
  // timeline boundary so the export covers either bed if the user hasn't
  // set a target/custom duration.
  const beds = session.sfxBeds[assetIdx] ?? [];
  const longestBedDuration = beds.length > 0
    ? beds.reduce((m, b) => Math.max(m, b.duration || 0), 0)
    : null;

  // Eine Entscheidung für Anzeige- und Export-Länge (Spec 2026-09-14).
  // Standard: Anzeige = getAssetTimelineDuration, Export weitet sich auf
  // den letzten Voice-Tail. Flex (Multi-Lingual, Asset ohne Video): Export
  // endet exakt am letzten Clip — kein Stille-Schwanz. Hat das Asset ein
  // Video, gilt auch in Multi-Lingual die Videodauer (Spec 2026-09-21 § 6).
  const length = resolveTimelineLength({
    flex: isFlexAsset(session.config, asset),
    assetIdx,
    videoDurations: video.durations,
    targetDuration: asset?.targetDuration,
    customDurations: session.customDurations,
    bedDuration: longestBedDuration,
    lastClipEnd: lastVoiceClipEnd(session, assetIdx, isStsActive),
  });
  const totalDuration = length.boundary;
  const exportTotalDuration = length.exportLength;

  const videoBlob = video.blobs[assetIdx] ?? null;
  const videoLocalSrc = video.sources[assetIdx] ?? null;
  const videoPath = asset?.videoPath ?? null;
  const videoRemoteSrc =
    session.toolId && videoPath
      ? streamVideoUrl(session.toolId, videoPath)
      : null;

  const videoSrc = videoLocalSrc ?? videoRemoteSrc ?? null;

  // Pass each bed's raw bytes + corresponding pre-duck gain through to the
  // renderer (max 2 entries). Index alignment: sfxBuffers[i] pairs with
  // sfxGains[i]. The export pipeline decodes inline against its
  // OfflineAudioContext and applies ducking automation per-track.
  const sfxBuffers: ArrayBuffer[] = beds.map((b) => b.buffer);
  const sfxGains: number[] = beds.map((_, idx) =>
    effectiveSfxVolume(mixer, false, (idx === 0 ? 0 : 1) as 0 | 1),
  );
  // Bed placement uses the DISPLAY asset length (`totalDuration`), not the
  // voice-tail-widened `exportTotalDuration` — the bed window is relative to
  // the asset, and voice overrun must not stretch a windowed bed.
  const sfxPlacements: BedPlacement[] = beds.map((b) =>
    resolveBedPlacement(b, totalDuration),
  );

  const exportInput: ExportInput = {
    totalDuration: exportTotalDuration,
    voiceClips,
    // Ein lokal geladenes Video geht als Blob in den Renderer, nicht als
    // blob:-URL: die Produktions-CSP (`connect-src 'self'`) blockt deren
    // fetch, der Video-Ton fehlte dadurch still im Premix (2026-09-21).
    videoSrc: videoBlob ?? videoSrc,
    sfxBuffers,
    sfxPlacements,
    voiceGain: effectiveVoiceVolume(mixer),
    // `effective*Volume(mixer, false)` yields the pre-duck volume —
    // the OfflineAudioContext render applies ducking itself via gain
    // automation, so we pass the base values here.
    videoGain: effectiveVideoVolume(mixer, false),
    sfxGains,
    duckingEnabled: mixer.videoDuckingEnabled,
    duckingStrength: mixer.videoDuckingStrength,
    // Die Stream-Route hängt am 12-h-Media-Session-Cookie. Läuft es ab (Tab
    // über Nacht offen), erneuern Premix-Render und Mux es bei einem 401
    // einmal selbst (Audit M18).
    ...(session.toolId && !videoBlob && !videoLocalSrc && videoRemoteSrc
      ? { refreshMediaSession: () => ensureMediaSession(session.toolId!) }
      : {}),
  };

  const effectiveType: 'tts' | 'sts' | undefined = asset
    ? isStsActive
      ? 'sts'
      : 'tts'
    : undefined;

  return {
    exportInput,
    assetTitle: asset?.title,
    assetType: asset?.assetType,
    effectiveType,
    videoSrc,
    videoBlob,
    videoPath,
    toolId: session.toolId,
  };
}
